import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ResultBadge } from "./ResultBadge";
import { PlayCircle, ShieldAlert, History } from "lucide-react";

export function PreFlightPanel({ cid, preflight, canOverride, onChange }) {
  const [busy, setBusy] = useState(false);
  const [ovOpen, setOvOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/campaigns/${cid}/preflight/run`);
      toast[r.data.status === "blocked" ? "error" : "success"](`Pre-flight ${r.data.status.replace(/_/g, " ")} · score ${r.data.score}%`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pre-flight failed"); } finally { setBusy(false); }
  };
  const override = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${cid}/preflight/override`, { reason });
      toast.success("Authorised exception recorded — execution unblocked");
      setOvOpen(false); setReason(""); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Override failed"); } finally { setBusy(false); }
  };
  const loadHistory = async () => {
    const r = await api.get(`/campaigns/${cid}/preflight`);
    setHistory(r.data);
  };

  const checks = preflight?.checks || [];
  return (
    <div className="space-y-5" data-testid="preflight-panel">
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-5">
        <ScoreRing score={preflight?.score ?? 0} />
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2">
            <span className="font-heading text-lg font-bold text-slate-900">Pre-Flight Validation · FR-01</span>
            {preflight && <ResultBadge value={preflight.status} data-testid="preflight-status" />}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {preflight ? `Last run ${new Date(preflight.executed_at).toLocaleString()} by ${preflight.executed_by} · plan v${preflight.plan_version} · ${preflight.counts.pass} pass / ${preflight.counts.fail} fail / ${preflight.counts.review} review`
              : "Not yet executed. Run to validate plan, PO, creator, deliverable and creative readiness before execution."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={run} disabled={busy} className="bg-[#0052FF] hover:bg-[#0043d1]" data-testid="preflight-run-btn">
            <PlayCircle size={16} className="mr-1" /> {preflight ? "Re-run pre-flight" : "Run pre-flight"}
          </Button>
          {canOverride && preflight?.status === "blocked" && (
            <Button variant="outline" onClick={() => setOvOpen(true)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="preflight-override-btn">
              <ShieldAlert size={16} className="mr-1" /> Authorise exception
            </Button>
          )}
          <Button variant="ghost" onClick={loadHistory} data-testid="preflight-history-btn"><History size={16} className="mr-1" /> History</Button>
        </div>
      </div>

      {checks.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2">Check</th><th className="px-4 py-2">Result</th><th className="px-4 py-2">Reason</th><th className="px-4 py-2">Recommended action</th></tr>
            </thead>
            <tbody>
              {checks.map((c) => (
                <tr key={c.code} className="border-t border-slate-100" data-testid={`preflight-check-${c.code}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-slate-800">{c.name}</div><div className="font-mono text-[11px] text-slate-400">{c.code}{c.mandatory ? " · mandatory" : ""}</div></td>
                  <td className="px-4 py-3"><ResultBadge value={c.result} /></td>
                  <td className="px-4 py-3 text-slate-700">{c.reason}</td>
                  <td className="px-4 py-3 text-slate-500">{c.recommended_action || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {history && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="preflight-history">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Run history ({history.length})</div>
          <ul className="divide-y divide-slate-100 text-sm">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between py-2">
                <span className="text-slate-600">{new Date(h.executed_at).toLocaleString()} · {h.executed_by} · plan v{h.plan_version}</span>
                <span className="flex items-center gap-2"><span className="font-mono text-xs">{h.score}%</span><ResultBadge value={h.status} /></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={ovOpen} onOpenChange={setOvOpen}>
        <DialogContent data-testid="preflight-override-dialog">
          <DialogHeader><DialogTitle>Authorise pre-flight exception</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Mandatory failures remain on record. Your name, role, timestamp and reason are written to the audit log and the Auditor is notified.</p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Business justification (min 10 characters)…" data-testid="preflight-override-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOvOpen(false)}>Cancel</Button>
            <Button onClick={override} disabled={busy || reason.trim().length < 10} className="bg-rose-600 hover:bg-rose-700" data-testid="preflight-override-confirm">Record exception</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScoreRing({ score }) {
  const r = 26, c = 2 * Math.PI * r, tone = score >= 90 ? "#10B981" : score >= 70 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative h-[68px] w-[68px]" data-testid="preflight-score">
      <svg width="68" height="68" viewBox="0 0 68 68">
        <circle cx="34" cy="34" r={r} stroke="#E5E7EB" strokeWidth="6" fill="none" />
        <circle cx="34" cy="34" r={r} stroke={tone} strokeWidth="6" fill="none" strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} strokeLinecap="round" transform="rotate(-90 34 34)" style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono text-sm font-bold text-slate-900">{score}%</div>
    </div>
  );
}
